import { ActivitySource } from './ActivitySource';
import { Enrollment } from './Enrollment';
import { isAdminRole, isInstructorRole, isPublisherRole, isStudentRole } from './Role';

export enum HighlightColor {
  YELLOW = '#FFD819',
  GREEN = '#9FFF9C',
  ORANGE = '#FFAD5E',
  PURPLE = '#D2A3FF',
  BLUE = '#ABCEFF',
  PINK = '#FFBCD9',
  PEACH = '#FF8978',
  BROWN = '#EAC28E',
}

export const SourceColor = {
  INSTRUCTOR: HighlightColor.BLUE,
  STUDENT: HighlightColor.YELLOW,
  AI: HighlightColor.PINK,
  PUBLISHER: HighlightColor.GREEN,
} satisfies Record<ActivitySource, HighlightColor>;

export type SourceColor = (typeof SourceColor)[keyof typeof SourceColor];

export const allHighlightColors = (): HighlightColor[] => {
  return [
    HighlightColor.YELLOW,
    HighlightColor.GREEN,
    HighlightColor.ORANGE,
    HighlightColor.PURPLE,
    HighlightColor.BLUE,
    HighlightColor.PINK,
    HighlightColor.PEACH,
    HighlightColor.BROWN,
  ];
};

const HIGHLIGHT_COLOR_TO_COURSE_SECTION_I18N_SUFFIX: Record<HighlightColor, string> = {
  [HighlightColor.YELLOW]: 'yellow',
  [HighlightColor.GREEN]: 'green',
  [HighlightColor.ORANGE]: 'orange',
  [HighlightColor.PURPLE]: 'purple',
  [HighlightColor.BLUE]: 'blue',
  [HighlightColor.PINK]: 'pink',
  [HighlightColor.PEACH]: 'peach',
  [HighlightColor.BROWN]: 'brown',
};

export const courseSectionHighlightColorNameKey = (c: HighlightColor): string =>
  `courseSectionHighlight.${HIGHLIGHT_COLOR_TO_COURSE_SECTION_I18N_SUFFIX[c]}`;

/** Case-insensitive match of stored course-section hex to a known swatch. */
export const parseHexToHighlightColor = (hex: string | undefined | null): HighlightColor | null => {
  if (!hex?.trim()) {
    return null;
  }
  const normalized = hex.trim().toLowerCase();
  for (const c of allHighlightColors()) {
    if (c.toLowerCase() === normalized) {
      return c;
    }
  }
  return null;
};

export const getAriaForColor = (color: HighlightColor | null): string => {
  if (color === HighlightColor.YELLOW) {
    return 'yellow';
  }

  if (color === HighlightColor.GREEN) {
    return 'green';
  }

  if (color === HighlightColor.ORANGE) {
    return 'orange';
  }

  if (color === HighlightColor.PURPLE) {
    return 'purple';
  }

  if (color === HighlightColor.BLUE) {
    return 'blue';
  }

  if (color === HighlightColor.PINK) {
    return 'pink';
  }

  if (color === HighlightColor.PEACH) {
    return 'peach';
  }

  if (color === HighlightColor.BROWN) {
    return 'brown';
  }

  return '';
};

export const availableHighlightColor = (enrollment?: Enrollment): HighlightColor[] => {
  const isAdmin = isAdminRole();
  const isPublisher = isPublisherRole();
  const isStudent = isStudentRole(enrollment);
  const isInstructor = isInstructorRole(enrollment);

  const canHighlightOnlyInGreen = isPublisher || (isAdmin && !enrollment);
  const canHighlightOnlyInBlue = isInstructor || (isAdmin && !!enrollment);

  if (canHighlightOnlyInGreen) {
    return [HighlightColor.GREEN];
  } else if (canHighlightOnlyInBlue) {
    return [HighlightColor.BLUE];
  } else if (isStudent) {
    return [
      HighlightColor.YELLOW,
      HighlightColor.ORANGE,
      HighlightColor.PURPLE,
      HighlightColor.PEACH,
      HighlightColor.BROWN,
    ];
  }

  return [];
};
